/**
 * My Commissions report — pure model.
 *
 * Shapes and transforms the LO-visible payload from get_my_commission_report
 * into payout ledgers, Net-LO-Locked pricing evidence, LO Profitability
 * residuals, and roll-up totals.
 * No company P&L lives here — the RPC never returns it.
 *
 * Reuses the canonical helpers so the math matches the rest of the app:
 *  - buildLoPayoutLedger  (banker-recon payout waterfall)
 *  - computeNetLoLocked   (Product & Pricing Net-LO-Locked / pricing-excess math)
 *  - isPayoutOutstanding  (0.01-tolerance owed check)
 */

import { format, isValid, parseISO } from 'date-fns';
import { getBusinessDateString } from '@/utils/dateHelpers';
import { buildLoPayoutLedger, type LoPayoutLedger } from '@/utils/banker-recon/loPayoutLedger';
import type { RecaptureActivity, RecaptureRecord } from '@/services/recaptureService';
import type { BonusReportMetadata, ProfitabilityBonus } from '@/services/loProfitabilityBonusService';
import type { ReportPeriod } from './date-range/reportPeriod';
import type { VolumeBonusActivity, VolumeBonusReportMetadata } from '@/services/loVolumeBonusService';
import {
  roundToCents,
  type LoCompensationVarianceBasis,
  type ReconLoProfitabilityComponent
} from '@/utils/banker-recon/loProfitability';
import {
  computeNetLoLocked,
  dollarsToPointsAt,
  pointsToDollarsAt,
  type NetLoLockedResult
} from '@/utils/pricing/netLoLocked';
import { payoutSettlement } from '@/utils/compensation/paymentCalculations';
import type { PayoutSettlement } from '../../../supabase/functions/_shared/payout-settlement';

// ---- RPC payload shapes -----------------------------------------------------

export type PayoutChannel = 'banked' | 'brokered' | 'override' | 'mentor' | 'volume_bonus';
export type ConcessionImpact = 'org_absorbed' | 'reduces_lo_payout' | 'pricing_only_borrower_credit';

export interface CommissionPlanBasis {
  has_comp_plan: boolean;
  banked_compensation: number | null;
  optimal_blue_current_percentage: number | null;
  schedule_name: string | null;
  schedule_scope: string | null;
}

export interface LedgerLineItem {
  label: string;
  amount: number;
}

export interface ConcessionEvidence {
  points: number;
  provenance: 'current' | 'frozen';
  impact: ConcessionImpact;
}

export interface PricingEvidence {
  buy_price: number;
  buy_price_provenance?: 'frozen' | 'current' | 'adjusted';
  discount_points: number;
  discount_provenance?: 'frozen' | 'current' | 'none';
  bpc_bps: number;
  bpc_provenance?: 'frozen' | 'current' | 'none';
  lender_credit_dollars: number;
  cure_dollars: number;
  locr_points: number;
  /** Pricing LO Comp adjustment bps — distinct from the payout compensation_value. */
  lo_comp_bps: number;
  concession_points: number;
  concessions: ConcessionEvidence[];
  /**
   * Current concessions that consumed Level 1 / Branch Management margin. These
   * are already in `concession_points`, so they are already included in
   * Net-LO-Locked / pricing excess.
   */
  recorded_override_release_bps?: number;
  /**
   * Additional aggregate benefit from built-in Level 1 / Branch Management margin
   * that was not paid out and was not already recorded as a concession release.
   * Recipient names / paid-out rows are intentionally not exposed here.
   */
  additional_override_release_bps?: number;
  additional_override_release_dollars?: number;
  /** Portion of unpaid override margin consumed by an investor purchase-price hit, not a concession. */
  override_reduction_offset_bps?: number;
  override_reduction_offset_dollars?: number;
}

export interface CommissionEarning {
  volume_bonus?: { plan_id: string; installment_number: number; loan_count: number; amount: number } | null;
  /** Stable payout row id — key for sort/filter/expand state, export traceability. */
  payout_id: string;
  /** Recipient (the earner). Present in admin all-records; = self in the self report. */
  lo_user_id?: string;
  /** Originating LO for override/mentor receipts (null for own banked/brokered rows). */
  source_officer_id?: string | null;
  source_officer_name?: string | null;
  loan_number: string;
  borrower_name: string | null;
  fund_date: string | null;
  purchase_date?: string | null;
  finalized_at: string | null;
  loan_amount: number;
  payout_type: PayoutChannel;
  status: 'approved' | 'paid' | 'pending';
  gross_amount: number;
  e_and_o_deduction: number;
  net_amount: number;
  payment_amount: number | null;
  recaptured_amount?: number;
  settlement?: PayoutSettlement;
  payment_check_number: string | null;
  payment_date: string | null;
  lo_commission_base: number | null;
  lo_reimbursements_total: number | null;
  source_type: string | null;
  /** Payout basis only — NOT the pricing LO-comp adjustment. */
  comp_basis: { compensation_type: 'flat_fee' | 'percentage'; compensation_value: number } | null;
  deductions: LedgerLineItem[];
  reimbursables: LedgerLineItem[];
  pricing_evidence: PricingEvidence | null;
  /** Banker recon backing this earning's loan (emitted on the banked LO row only). */
  banker_recon_id?: string | null;
  /**
   * Frozen signed LO Profitability from the finalized banked recon (Banker Recon
   * "LO Profitability"). Future-only: null on historical / never-saved rows, so
   * those fall back to the tuned pricing math. May be negative (carries into the
   * sum). Only emitted on the banked LO payout row (override/mentor rows = null).
   */
  finalized_lo_profitability_net?: number | null;
  finalized_lo_profitability_gross?: number | null;
  finalized_lo_profitability_components?: ReconLoProfitabilityComponent[] | null;
}

export interface CommissionContextRow {
  loan_number: string;
  fund_date: string | null;
  payout_type: PayoutChannel;
  status: string;
  net_amount: number;
  recipient_name: string;
  /** The LO who generated this split — splits attach here, not by loan number alone. */
  source_officer_id?: string | null;
}

export interface MyCommissionReport extends BonusReportMetadata, VolumeBonusReportMetadata {
  plan: CommissionPlanBasis | null;
  earnings: CommissionEarning[];
  context: CommissionContextRow[];
}

/** A user in the admin all-records set (LOs, the admin, and any historical participant). */
export interface ReportUser {
  bonus_eligible?: boolean;
  user_id: string;
  name: string | null;
  email: string | null;
  employment_status: string | null;
  /** null for recapture-only recipients whose plan was not returned by the report. */
  has_comp_plan: boolean | null;
  optimal_blue_current_percentage: number | null;
  banked_compensation: number | null;
  schedule_name: string | null;
  schedule_scope: string | null;
}

export interface AdminCommissionReport extends BonusReportMetadata, VolumeBonusReportMetadata {
  report_users: ReportUser[];
  earnings: CommissionEarning[];
  context: CommissionContextRow[];
}

// ---- helpers ----------------------------------------------------------------

const num = (v: number | null | undefined): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : 0;

const positiveMoney = (v: number): number => (v > 0.01 ? v : 0);

/**
 * Known investor-switch repricing cases where the lower final investor price
 * should not be interpreted as a company concession to the LO.
 */
const IMPLIED_CONCESSION_EXCLUDED_LOANS = new Set(['LW25121501']);
const PROFITABILITY_RESET_START_DATE = '2026-07-01';

const CHANNEL_LABELS: Record<PayoutChannel, string> = {
  banked: 'Banked',
  brokered: 'Brokered',
  override: 'Override',
  mentor: 'Mentor',
  volume_bonus: 'Volume Bonus'
};

export const channelLabel = (c: PayoutChannel): string => CHANNEL_LABELS[c] ?? c;

/** Keep an installment under its own LO's banked row, including in all-user views.
 * A retained installment remains visible by itself while its recon is reopened. */
export function groupCommissionEarnings(earnings: CommissionEarning[]): { earning: CommissionEarning; installments: CommissionEarning[] }[] {
  const key = (earning: CommissionEarning) => `${earning.lo_user_id ?? ''}:${earning.loan_number}`;
  const bankedKeys = new Set(earnings.filter(earning => earning.payout_type === 'banked').map(key));
  const installments = new Map<string, CommissionEarning[]>();
  for (const earning of earnings) {
    if (earning.payout_type === 'volume_bonus' && bankedKeys.has(key(earning))) {
      const list = installments.get(key(earning)) ?? [];
      list.push(earning);
      installments.set(key(earning), list);
    }
  }
  return earnings.filter(earning => earning.payout_type !== 'volume_bonus' || !bankedKeys.has(key(earning)))
    .map(earning => {
      const children = earning.payout_type === 'banked' ? installments.get(key(earning)) ?? [] : [];
      // Defensive against a duplicate ordinary row; never repeat the cash credit.
      if (children.length) {
        installments.delete(key(earning));
      }
      return { earning, installments: children };
    });
}

/**
 * Banked reimbursables, reconciled to the payout's `lo_reimbursements_total`.
 * Older finalized banked payouts carry the total without per-item `reimbursable`
 * flags, so the itemized list under-sums and the ledger would fall short of
 * net_amount. When the itemized lines don't reconcile to the total, fall back to
 * a single "Reimbursements" line for the recorded total.
 */
function reconcileReimbursables(e: CommissionEarning): LedgerLineItem[] {
  const itemized = e.reimbursables.map((r) => ({ label: r.label, amount: num(r.amount) }));
  const itemizedSum = itemized.reduce((sum, r) => sum + r.amount, 0);
  const total = num(e.lo_reimbursements_total);
  if (Math.abs(itemizedSum - total) <= 0.01) {
    return itemized;
  }
  return total !== 0 ? [{ label: 'Reimbursements', amount: total }] : [];
}

/**
 * The frozen payout ledger (what the LO is actually paid). Reconciles so that
 * `ledger.netPayout === earning.net_amount` for every channel:
 *  - banked:  pre-deduction base − LOCR/deductions + reimbursables − E&O
 *  - brokered: lo_commission_base (post-split, pre-reimb) + reimbursements − E&O
 *             (gross_amount already includes reimbursements — don't double-count)
 *  - override/mentor received: gross_amount − E&O
 */
export function buildEarningLedger(e: CommissionEarning): LoPayoutLedger {
  const loanAmount = num(e.loan_amount);
  const eAndO = num(e.e_and_o_deduction);

  if (e.payout_type === 'volume_bonus') {
    const ledger = buildLoPayoutLedger({
      loanAmount, baseCompensation: 0, deductions: [], reimbursables: [], eAndODeduction: 0,
      volumeBonusAmount: num(e.net_amount),
      volumeBonusLabel: e.volume_bonus ? `Volume Bonus — installment ${e.volume_bonus.installment_number} of ${e.volume_bonus.loan_count}` : 'Volume Bonus'
    });
    return { ...ledger, lines: ledger.lines.filter(line => line.type !== 'base') };
  }

  if (e.payout_type === 'banked' && e.comp_basis) {
    const base =
      e.comp_basis.compensation_type === 'flat_fee'
        ? num(e.comp_basis.compensation_value)
        : loanAmount > 0
          ? (loanAmount * num(e.comp_basis.compensation_value)) / 100
          : 0;
    return buildLoPayoutLedger({
      loanAmount,
      baseCompensation: base,
      deductions: e.deductions.map((d) => ({ label: d.label, amount: num(d.amount) })),
      reimbursables: reconcileReimbursables(e),
      eAndODeduction: eAndO
    });
  }

  if (e.payout_type === 'brokered') {
    const reimb = num(e.lo_reimbursements_total);
    const base = e.lo_commission_base !== null ? num(e.lo_commission_base) : num(e.gross_amount) - reimb;
    return buildLoPayoutLedger({
      loanAmount,
      baseCompensation: base,
      deductions: [],
      reimbursables: reimb !== 0 ? [{ label: 'Reimbursements', amount: reimb }] : [],
      eAndODeduction: eAndO
    });
  }

  // override / mentor received from another LO's deal — gross less E&O.
  return buildLoPayoutLedger({
    loanAmount,
    baseCompensation: num(e.gross_amount),
    deductions: [],
    reimbursables: [],
    eAndODeduction: eAndO
  });
}

export interface EarningLocrBreakdown {
  hasLoCompMargin: boolean;
  explicitPoints: number;
  implicitPoints: number;
  appliedPoints: number;
  displayPoints: number;
}

export function computeEarningLocrBreakdown(e: CommissionEarning): EarningLocrBreakdown | null {
  const pe = e.pricing_evidence;
  if (!pe) {
    return null;
  }

  const hasLoCompMargin = num(pe.lo_comp_bps) > 0.0005;
  const explicitPoints = num(pe.locr_points);
  const loCompMarginPoints = hasLoCompMargin ? num(pe.lo_comp_bps) / 100 : 0;
  const paidCompPoints =
    e.payout_type === 'banked' && e.comp_basis?.compensation_type === 'percentage'
      ? num(e.comp_basis.compensation_value)
      : loCompMarginPoints;
  const appliedExplicitPoints = hasLoCompMargin ? explicitPoints : 0;
  const implicitPoints = hasLoCompMargin
    ? Math.max(loCompMarginPoints - paidCompPoints - appliedExplicitPoints, 0)
    : 0;
  const appliedPoints = appliedExplicitPoints + implicitPoints;

  return {
    hasLoCompMargin,
    explicitPoints,
    implicitPoints,
    appliedPoints,
    displayPoints: hasLoCompMargin ? appliedPoints : explicitPoints
  };
}

/**
 * Net LO Locked waterfall for an earning, from its (frozen-first) pricing
 * evidence. Concessions (provenance current) DO feed this — matching Product &
 * Pricing — but never the frozen payout ledger above. Null for non-banked rows.
 */
export function computeEarningNetLoLocked(e: CommissionEarning): NetLoLockedResult | null {
  const pe = e.pricing_evidence;
  if (!pe) {
    return null;
  }
  const loanAmount = num(e.loan_amount);
  const locr = computeEarningLocrBreakdown(e);
  return computeNetLoLocked({
    basePriceLocked: num(pe.buy_price),
    discountPoints: num(pe.discount_points),
    concessionPoints: num(pe.concession_points),
    // LOCR only releases pricing when a frozen LO Compensation margin row exists;
    // otherwise there is nothing to add back and it double-counts residuals
    // (e.g. LW26032190). Some finalized recons reduced compensation directly
    // in `compensation_value` instead of a separate LOCR line item (e.g.
    // LW26042311), so derive that implicit release from built-in LO comp less
    // paid comp less any explicit LOCR already returned by the RPC.
    locrPoints: locr?.appliedPoints ?? 0,
    lenderCreditPoints: dollarsToPointsAt(loanAmount, num(pe.lender_credit_dollars)),
    curePoints: dollarsToPointsAt(loanAmount, num(pe.cure_dollars)),
    loanAmount
  });
}

/** Raw above-par pricing excess for an earning. Null when pricing evidence is unavailable. */
export function computeEarningPricingExcess(e: CommissionEarning): number | null {
  return computeEarningNetLoLocked(e)?.loProfitDollars ?? null;
}

export interface NoLoCompMarginReconciliation {
  netLoLocked: NetLoLockedResult;
  pricingExcess: number;
  recordedOverrideReleaseBps: number;
  recordedOverrideReleaseDollars: number;
  additionalOverrideReleaseBps: number;
  additionalOverrideReleaseDollars: number;
  overrideReductionOffsetBps: number;
  overrideReductionOffsetDollars: number;
  borrowerPaidCompBps: number;
  borrowerPaidCompDollars: number;
  passThroughReimbursements: number;
  loPaidAmount: number;
  pricingSupport: number;
  impliedConcessionDollars: number;
  loProfitability: number;
}

/**
 * Final residual formula for banked rows with no frozen LO Compensation
 * margin row:
 *
 *   pricing excess
 * + borrower-paid comp
 * - LO paid economic amount
 * = LO Profitability
 *
 * When LO paid economic amount is greater than pricing support, the difference
 * is treated as an implied concession instead of exposing internal override /
 * investor-hit mechanics.
 */
export function computeNoLoCompMarginReconciliation(
  e: CommissionEarning
): NoLoCompMarginReconciliation | null {
  const pe = e.pricing_evidence;
  if (e.payout_type !== 'banked' || !pe || num(pe.lo_comp_bps) > 0.0005) {
    return null;
  }

  const netLoLocked = computeEarningNetLoLocked(e);
  if (!netLoLocked) {
    return null;
  }

  const loanAmount = num(e.loan_amount);
  const recordedOverrideReleaseBps = num(pe.recorded_override_release_bps);
  const additionalOverrideReleaseBps = num(pe.additional_override_release_bps);
  const additionalOverrideReleaseDollars =
    pe.additional_override_release_dollars !== undefined
      ? num(pe.additional_override_release_dollars)
      : pointsToDollarsAt(loanAmount, additionalOverrideReleaseBps / 100);
  const overrideReductionOffsetBps = num(pe.override_reduction_offset_bps);
  const overrideReductionOffsetDollars =
    pe.override_reduction_offset_dollars !== undefined
      ? num(pe.override_reduction_offset_dollars)
      : pointsToDollarsAt(loanAmount, overrideReductionOffsetBps / 100);
  const recordedOverrideReleaseDollars = pointsToDollarsAt(loanAmount, recordedOverrideReleaseBps / 100);
  const borrowerPaidCompBps = num(pe.bpc_bps);
  const borrowerPaidCompDollars = pointsToDollarsAt(loanAmount, borrowerPaidCompBps / 100);
  const passThroughReimbursements = computePassThroughReimbursements(e);
  const loPaidAmount = num(e.gross_amount) - passThroughReimbursements;
  const pricingExcess = netLoLocked.loProfitDollars;
  const pricingSupport = pricingExcess + borrowerPaidCompDollars;
  const suppressImpliedConcession = IMPLIED_CONCESSION_EXCLUDED_LOANS.has(e.loan_number);
  const impliedConcessionDollars = suppressImpliedConcession ? 0 : positiveMoney(loPaidAmount - pricingSupport);
  const loProfitability = positiveMoney(pricingSupport - loPaidAmount);

  return {
    netLoLocked,
    pricingExcess,
    recordedOverrideReleaseBps,
    recordedOverrideReleaseDollars,
    additionalOverrideReleaseBps,
    additionalOverrideReleaseDollars,
    overrideReductionOffsetBps,
    overrideReductionOffsetDollars,
    borrowerPaidCompBps,
    borrowerPaidCompDollars,
    passThroughReimbursements,
    loPaidAmount,
    pricingSupport,
    impliedConcessionDollars,
    loProfitability
  };
}

export function computeEarningLoProfitability(e: CommissionEarning): number | null {
  if (e.payout_type === 'volume_bonus') {
    return null;
  }
  // Future-only connection: a banked recon finalized under the routing feature
  // stores its signed LO Profitability (may be negative — it carries into the
  // sum). Prefer it verbatim so the card matches the recon exactly. Historical /
  // never-saved rows have null here and fall back to the tuned pricing math
  // below (Case 1 pricing-excess / Case 2 residual), unchanged.
  // Defense-in-depth: only the banked LO row carries this (the RPC also gates on
  // payout_type='banked' AND recipient_id=lo_user_id) — never let an override/
  // mentor row inherit a stored net even if one leaks onto it.
  if (
    e.payout_type === 'banked' &&
    e.finalized_lo_profitability_net !== null &&
    e.finalized_lo_profitability_net !== undefined
  ) {
    return e.finalized_lo_profitability_net;
  }

  const noLoCompMargin = computeNoLoCompMarginReconciliation(e);
  if (noLoCompMargin) {
    return noLoCompMargin.loProfitability;
  }

  return computeEarningPricingExcess(e);
}

/** A self activity has no recipient field; its caller supplies the authorized account ID. */
export function earningProfitabilityRecaptures(
  e: CommissionEarning, records: RecaptureActivity[] | null, recipientId?: string
): RecaptureActivity[] {
  if (computeEarningLoProfitability(e) === null) {
    return [];
  }
  const earningRecipient = e.lo_user_id ?? recipientId;
  return (records ?? []).filter(record => {
    const recordRecipient = 'recipient_id' in record ? record.recipient_id : recipientId;
    return Boolean(earningRecipient && recordRecipient === earningRecipient)
      && record.status === 'submitted' && record.loan_number === e.loan_number
      && record.allocations.some(allocation => allocation.kind === 'profitability' && allocation.amount !== 0);
  });
}

const absorbedByRecord = (record: RecaptureActivity): number => record.allocations.reduce((sum, allocation) =>
  allocation.kind === 'profitability' ? sum + Math.round(allocation.amount * 100) : sum, 0) / 100;

/** Lifetime loan display only. Period totals continue using the posting-date event ledger below. */
export function computeDisplayedEarningLoProfitability(
  e: CommissionEarning, records: RecaptureActivity[] | null, recipientId?: string
): number | null {
  const raw = computeEarningLoProfitability(e);
  const absorbed = earningProfitabilityRecaptures(e, records, recipientId).reduce((sum, record) => sum + Math.round(absorbedByRecord(record) * 100), 0);
  return raw === null || absorbed === 0 ? raw : (Math.round(raw * 100) - absorbed) / 100;
}

function recapturePostedDate(record: RecaptureActivity): string | null {
  const date = record.submitted_at ? parseISO(record.submitted_at) : null;
  return date && isValid(date) ? getBusinessDateString(date) : null;
}

/** One explanation for the full loaded population, independent of toolbar filters or installments. */
export function profitabilityReconciliation(
  earnings: CommissionEarning[], records: RecaptureActivity[] | null, period: ReportPeriod, recipientId?: string
): string {
  if (records === null) {
    return '';
  }
  const matched = new Map(earnings.flatMap(e => earningProfitabilityRecaptures(e, records, recipientId)).map(record => [record.id, record]));
  const dates = new Set<string>();
  let cents = 0;
  for (const record of matched.values()) {
    if (recapturePostedInPeriod(record, period)) {
      continue;
    }
    const date = recapturePostedDate(record);
    if (!date) {
      return 'Recapture posting date unavailable; period reconciliation could not be verified.';
    }
    dates.add(date);
    cents += Math.round(absorbedByRecord(record) * 100);
  }
  if (!cents) {
    return '';
  }
  const amount = (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const labels = [...dates].sort().map(date => format(parseISO(date), 'MMM d, yyyy'));
  const postedDates = labels.length > 2 ? `${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}` : labels.join(' and ');
  return labels.length === 1
    ? `Includes ${amount} absorbed by a recapture posted ${labels[0]}, counted in that period.`
    : `Includes ${amount} absorbed by recaptures posted ${postedDates}, counted in those periods.`;
}

export interface EarningTieOutLine {
  label: string;
  note?: string;
  provenance?: 'frozen' | 'current' | 'adjusted';
  delta: number;
  running: number | null;
  kind: 'term' | 'info' | 'total';
  /** Secondary conversion supplied by the model, never calculated in the renderer. */
  dollars?: number;
  detail?: boolean;
}

export interface EarningTieOutSegment {
  id: 'price' | 'profitability' | 'payout';
  title: string;
  unit: 'points' | 'currency';
  lines: EarningTieOutLine[];
  note?: string;
}

/** Display waterfall. Named totals always come from the canonical outputs, not accumulated display terms. */
export function buildEarningTieOut(e: CommissionEarning, options: {
  maskPricingEvidence: boolean;
  showSplits: boolean;
  recaptureRecords: RecaptureActivity[] | null;
  recipientId?: string;
}): EarningTieOutSegment[] {
  const segments: EarningTieOutSegment[] = [];
  const pe = e.pricing_evidence;
  const stored = e.payout_type === 'banked' && e.finalized_lo_profitability_net !== null && e.finalized_lo_profitability_net !== undefined;
  const components = (e.finalized_lo_profitability_components ?? []).filter(component => component.effectAmount !== 0);
  const locr = computeEarningLocrBreakdown(e);
  const netLocked = computeEarningNetLoLocked(e);
  const noMargin = computeNoLoCompMarginReconciliation(e);
  const variance = getEarningLoCompensationVarianceBasis(e);
  const rawProfit = computeEarningLoProfitability(e);
  const displayedProfit = computeDisplayedEarningLoProfitability(e, options.recaptureRecords, options.recipientId);
  const price: EarningTieOutSegment = { id: 'price', title: 'Price', unit: 'points', lines: [] };
  if (pe && !options.maskPricingEvidence && e.payout_type !== 'volume_bonus') {
    const appliedCleanLocr = Math.max(0, Math.min(num(pe.locr_points), num(pe.lo_comp_bps) / 100));
    const clean = computeNetLoLocked({ basePriceLocked: pe.buy_price, discountPoints: pe.discount_points,
      concessionPoints: 0, locrPoints: appliedCleanLocr, lenderCreditPoints: 0, curePoints: 0, loanAmount: e.loan_amount });
    const cleanDollars = roundToCents(pointsToDollarsAt(e.loan_amount, clean.netLoLocked - 100));
    const ties = !stored || (e.finalized_lo_profitability_gross !== null && e.finalized_lo_profitability_gross !== undefined
      && Math.abs(Math.round(cleanDollars * 100) - Math.round(e.finalized_lo_profitability_gross * 100)) <= 1);
    let running = 0;
    const priceLine = (label: string, delta: number, term: boolean, extra: Partial<EarningTieOutLine> = {}) => {
      if (term && ties) {
        running += delta;
      }
      price.lines.push({ label, delta, running: ties ? running : null, kind: term && ties ? 'term' : 'info',
        dollars: pointsToDollarsAt(e.loan_amount, delta), ...extra });
    };
    priceLine('Total price locked', pe.buy_price, true, { provenance: pe.buy_price_provenance ?? 'frozen', dollars: undefined });
    if (pe.discount_points !== 0) {
      priceLine('Discount points', pe.discount_points, true, { provenance: pe.discount_provenance === 'current' ? 'current' : 'frozen' });
    }
    if (pe.bpc_bps !== 0) {
      priceLine('Borrower-paid comp', pe.bpc_bps / 100, false, { provenance: pe.bpc_provenance === 'current' ? 'current' : 'frozen' });
    }
    if (locr?.hasLoCompMargin && !variance) {
      priceLine('LO comp built into margin', pe.lo_comp_bps / 100, false, { provenance: 'frozen' });
    }
    const applied = stored ? appliedCleanLocr : locr?.appliedPoints ?? 0;
    if (applied !== 0 || locr?.displayPoints) {
      priceLine(applied ? 'Applied LO comp reduction' : 'LO comp reduction (not added)', applied || locr?.displayPoints || 0,
        applied !== 0, { provenance: 'frozen', note: applied ? undefined : 'No LO comp margin' });
    }
    const concessionInBridge = stored && components.some(component => component.type === 'company_concession');
    if (!concessionInBridge && pe.concession_points !== 0) {
      const impactLabels: Record<ConcessionImpact, string> = { org_absorbed: 'Organization absorbed',
        reduces_lo_payout: 'Reduces LO payout', pricing_only_borrower_credit: 'Borrower credit (pricing only)' };
      const reconciles = Math.abs(pe.concessions.reduce((sum, concession) => sum + concession.points, 0) - pe.concession_points) < 0.0000001;
      if (pe.concessions.length && reconciles) {
        for (const concession of pe.concessions.filter(item => item.points !== 0)) {
          priceLine('Actual concession', concession.points, !stored, { provenance: concession.provenance, note: impactLabels[concession.impact] });
        }
      } else {
        priceLine('Actual concession', pe.concession_points, !stored, {
          provenance: pe.concessions.some(concession => concession.provenance === 'current') ? 'current' : 'frozen',
          note: [...new Set(pe.concessions.map(concession => impactLabels[concession.impact]))].join(' · ') || undefined
        });
      }
    }
    if (pe.lender_credit_dollars !== 0) {
      priceLine('Lender credit deduction', -dollarsToPointsAt(e.loan_amount, pe.lender_credit_dollars), !stored, { provenance: 'frozen' });
    }
    if (pe.cure_dollars !== 0) {
      priceLine('Cure deduction', -dollarsToPointsAt(e.loan_amount, pe.cure_dollars), !stored, { provenance: 'frozen' });
    }
    if (ties) {
      const total = stored ? clean.netLoLocked : netLocked!.displayedNetLoLocked;
      price.lines.push({ label: stored ? 'Clean LO price' : 'Net LO Locked Price', delta: total, running: total, kind: 'total' });
    } else {
      price.note = 'Pricing evidence does not reconcile to stored gross. The profitability calculation starts from the stored amount.';
    }
    segments.push(price);
  }
  if (rawProfit !== null) {
    const profit: EarningTieOutSegment = { id: 'profitability', title: 'Profitability', unit: 'currency', lines: [] };
    let running = 0;
    const line = (label: string, delta: number, extra: Partial<EarningTieOutLine> = {}) => {
      if (extra.kind !== 'info') {
        running += delta;
      }
      profit.lines.push({ label, delta, running, kind: 'term', ...extra });
    };
    if (!options.maskPricingEvidence) {
      if (stored) {
        if (e.finalized_lo_profitability_gross !== null && e.finalized_lo_profitability_gross !== undefined) {
          line('Gross pricing gain', e.finalized_lo_profitability_gross, { provenance: 'frozen' });
          for (const component of components) {
            line(component.target === 'base_comp' ? `${component.label} (to base comp)` : component.label, component.effectAmount, { provenance: 'frozen' });
            if (component.type === 'lo_compensation_variance' && variance) {
              for (const [label, amount, points] of [['Plan compensation', variance.planAmount, variance.planPoints],
                ['Built into pricing', variance.builtInAmount, variance.builtInPoints], ['Signed variance', variance.varianceAmount, variance.variancePoints]] as const) {
                line(label, amount, { kind: 'info', detail: true, note: `${points.toFixed(3)} pts`, provenance: 'frozen' });
              }
              profit.note = 'LO Profitability scorecard only; payout is not deducted again.';
            }
          }
        }
      } else if (netLocked) {
        line('Pricing excess', netLocked.loProfitDollars, { note: `${(netLocked.displayedNetLoLocked - 100).toFixed(3)} pts above par` });
        if (noMargin) {
          if (noMargin.borrowerPaidCompDollars !== 0) {
            line('Borrower-paid comp', noMargin.borrowerPaidCompDollars);
          }
          if (noMargin.passThroughReimbursements !== 0) {
            line('Pass-through reimbursements excluded', noMargin.passThroughReimbursements, { kind: 'info' });
          }
          line('LO paid amount', -noMargin.loPaidAmount);
          if (noMargin.impliedConcessionDollars !== 0) {
            line('Implied concession', noMargin.impliedConcessionDollars);
          }
          if (Math.abs(running - noMargin.loProfitability) > 0.0000001) {
            profit.note = running < -0.01 ? 'This historical profitability total is floored at zero.'
              : 'Residuals of one cent or less are shown as zero.';
          }
        }
      }
    }
    // Masks and older stored rows may have no bridge; start deductions at their canonical raw value.
    running = rawProfit;
    for (const record of earningProfitabilityRecaptures(e, options.recaptureRecords, options.recipientId)) {
      const date = recapturePostedDate(record);
      line('Absorbed by profitability', -absorbedByRecord(record), { note: `Recapture · ${date ? format(parseISO(date), 'MMM d, yyyy') : 'Posting date unavailable'}` });
    }
    profit.lines.push({ label: 'LO Profitability', delta: displayedProfit!, running: displayedProfit!, kind: 'total' });
    segments.push(profit);
  }
  const ledger = buildEarningLedger(e);
  const payout: EarningTieOutSegment = { id: 'payout', title: 'Payout ledger', unit: 'currency', lines: [] };
  let running = 0;
  for (const item of ledger.lines) {
    const total = item.type === 'subtotal' || item.type === 'total';
    running = total ? item.amount : running + item.amount;
    const label = item.type === 'base'
      ? e.payout_type === 'brokered' ? 'LO commission (after split)'
        : e.comp_basis?.compensation_type === 'percentage' ? `Gross commission (${e.comp_basis.compensation_value.toFixed(3)}% of loan)` : item.label
      : item.label;
    payout.lines.push({ label, delta: item.amount, running, kind: total ? 'total' : 'term', provenance: 'frozen' });
  }
  const settlement = earningSettlement(e);
  running = ledger.netPayout - settlement.cash;
  payout.lines.push({ label: 'Cash paid', delta: -settlement.cash, running, kind: 'term',
    note: [e.payment_check_number ? `Latest check #${e.payment_check_number}` : '',
      e.payment_date ? format(parseISO(e.payment_date.slice(0, 10)), 'MMM d, yyyy') : ''].filter(Boolean).join(' · ') || undefined });
  if (settlement.recaptured !== 0) {
    running -= settlement.recaptured;
    payout.lines.push({ label: 'Commission deducted for recapture', delta: -settlement.recaptured, running, kind: 'term' });
  }
  payout.lines.push({ label: 'Remaining balance', delta: earningBalance(e), running: earningBalance(e), kind: 'total' });
  segments.push(payout);
  return segments;
}

/**
 * Stored plan-versus-built-in bridge for a finalized banked LO row. Malformed
 * historical JSON fails closed so detail/export surfaces never print NaN.
 */
export function getEarningLoCompensationVarianceBasis(
  e: CommissionEarning
): LoCompensationVarianceBasis | null {
  if (e.payout_type !== 'banked' || !Array.isArray(e.finalized_lo_profitability_components)) {
    return null;
  }

  const basis = e.finalized_lo_profitability_components.find(
    component => component.type === 'lo_compensation_variance'
  )?.varianceBasis;
  if (!basis) {
    return null;
  }

  const values = [
    basis.planAmount,
    basis.planPoints,
    basis.builtInAmount,
    basis.builtInPoints,
    basis.varianceAmount,
    basis.variancePoints
  ];
  return values.every(Number.isFinite) ? basis : null;
}

export function isPreJulyResetProfitabilityRow(e: CommissionEarning): boolean {
  const fundDate = e.fund_date?.slice(0, 10);
  return (
    e.payout_type === 'banked' &&
    fundDate !== undefined &&
    fundDate < PROFITABILITY_RESET_START_DATE &&
    e.finalized_lo_profitability_net === 0 &&
    e.finalized_lo_profitability_gross === 0 &&
    Array.isArray(e.finalized_lo_profitability_components) &&
    e.finalized_lo_profitability_components.length === 0
  );
}

export interface ChannelTotal {
  channel: PayoutChannel;
  count: number;
  earned: number;
}

export interface CommissionTotals {
  totalEarned: number;
  totalPaid: number;
  totalRecaptured: number;
  /** Signed net minus cash and active recapture, including LO credits. */
  outstanding: number;
  loanCount: number;
  /** Current concession dollars, deduped by loan_number. */
  concessionsTotal: number;
  /** Distinct loan files with current concession points. */
  concessionFileCount: number;
  /** Σ row-level LO Profitability. Non-banked rows without pricing evidence are excluded. */
  loProfitability: number;
  byChannel: ChannelTotal[];
}

export interface CurrentConcessionSummary {
  concessionsTotal: number;
  concessionFileCount: number;
}

export interface EarningConcessionBreakdown {
  actualConcessionDollars: number;
  impliedConcessionDollars: number;
  totalConcessionDollars: number;
  actualConcessionPoints: number;
  pricingSupport: number | null;
  passThroughReimbursements: number;
  loPaidEconomic: number | null;
}

const PASS_THROUGH_REIMBURSEMENT_LABELS = new Set(['credit report']);
const GENERIC_REIMBURSEMENT_LABEL = 'reimbursements';

function computePassThroughReimbursements(e: CommissionEarning): number {
  const reconciled = reconcileReimbursables(e);
  if (
    reconciled.length === 1 &&
    reconciled[0].label.trim().toLowerCase() === GENERIC_REIMBURSEMENT_LABEL
  ) {
    return num(reconciled[0].amount);
  }

  return reconciled.reduce((sum, r) => {
    const label = r.label.trim().toLowerCase();
    return PASS_THROUGH_REIMBURSEMENT_LABELS.has(label) ? sum + num(r.amount) : sum;
  }, 0);
}

export function computeEarningConcessionBreakdown(e: CommissionEarning): EarningConcessionBreakdown {
  if (e.payout_type === 'volume_bonus') {
    return { actualConcessionDollars: 0, impliedConcessionDollars: 0, totalConcessionDollars: 0,
      actualConcessionPoints: 0, pricingSupport: null, passThroughReimbursements: 0, loPaidEconomic: null };
  }
  const loanAmount = num(e.loan_amount);
  const actualConcessionPoints = num(e.pricing_evidence?.concession_points);
  const actualConcessionDollars =
    actualConcessionPoints > 0 && loanAmount > 0 ? pointsToDollarsAt(loanAmount, actualConcessionPoints) : 0;
  const noLoCompRecon = computeNoLoCompMarginReconciliation(e);
  const impliedConcessionDollars = noLoCompRecon?.impliedConcessionDollars ?? 0;

  return {
    actualConcessionDollars,
    impliedConcessionDollars,
    totalConcessionDollars: actualConcessionDollars + impliedConcessionDollars,
    actualConcessionPoints,
    pricingSupport: noLoCompRecon?.pricingSupport ?? null,
    passThroughReimbursements: noLoCompRecon?.passThroughReimbursements ?? 0,
    loPaidEconomic: noLoCompRecon?.loPaidAmount ?? null
  };
}

/**
 * Combined concessions are loan-level. Deduplicate by loan_number so multiple
 * earning rows for the same file cannot inflate the summary card.
 */
export function computeCurrentConcessionSummary(earnings: CommissionEarning[]): CurrentConcessionSummary {
  const byLoan = new Map<string, number>();

  for (const e of earnings) {
    const loanNumber = e.loan_number?.trim();
    if (!loanNumber || byLoan.has(loanNumber)) {
      continue;
    }

    const concession = computeEarningConcessionBreakdown(e);
    if (concession.totalConcessionDollars <= 0) {
      continue;
    }

    byLoan.set(loanNumber, concession.totalConcessionDollars);
  }

  return {
    concessionsTotal: Array.from(byLoan.values()).reduce((sum, amount) => sum + amount, 0),
    concessionFileCount: byLoan.size
  };
}

export function computeCommissionTotals(earnings: CommissionEarning[]): CommissionTotals {
  let totalEarned = 0;
  let totalPaid = 0;
  let totalRecaptured = 0;
  let outstanding = 0;
  let loProfitability = 0;
  const channelMap = new Map<PayoutChannel, ChannelTotal>();
  const concessions = computeCurrentConcessionSummary(earnings);

  for (const e of earnings) {
    const net = num(e.net_amount);
    const paid = num(e.payment_amount);
    totalEarned += net;
    totalPaid += paid;
    const settlement = earningSettlement(e);
    totalRecaptured += settlement.recaptured;
    outstanding += settlement.remaining;
    const prof = computeEarningLoProfitability(e);
    if (prof !== null) {
      loProfitability += prof;
    }
    const existing = channelMap.get(e.payout_type) ?? { channel: e.payout_type, count: 0, earned: 0 };
    existing.count += 1;
    existing.earned += net;
    channelMap.set(e.payout_type, existing);
  }

  return {
    totalEarned,
    totalPaid,
    totalRecaptured,
    outstanding,
    loanCount: earnings.filter(earning => earning.payout_type !== 'volume_bonus').length,
    concessionsTotal: concessions.concessionsTotal,
    concessionFileCount: concessions.concessionFileCount,
    loProfitability,
    byChannel: Array.from(channelMap.values())
  };
}

export function earningSettlement(e: CommissionEarning): PayoutSettlement {
  return payoutSettlement(num(e.net_amount), num(e.payment_amount), num(e.recaptured_amount));
}

export interface AdjustedCommissionTotals extends CommissionTotals {
  profitabilityVolumeBonusAdjustment?: number;
  profitabilityBonuses: number;
  /** null means activity could not be loaded; the earned figure is unadjusted. */
  profitabilityRecaptured: number | null;
}

export function recapturePostedInPeriod(record: RecaptureActivity, period: ReportPeriod): boolean {
  if (!record.submitted_at) {
    return false;
  }
  const submitted = parseISO(record.submitted_at);
  if (!isValid(submitted)) {
    return false;
  }
  // Picker endpoints are calendar dates; postings are instants on Pacific business days.
  const day = getBusinessDateString(submitted);
  return day >= format(period.from, 'yyyy-MM-dd') && day <= format(period.to, 'yyyy-MM-dd');
}

/** Period totals only: per-loan profitability and cash settlement stay unchanged. */
export function bonusPeriodContribution(bonus: ProfitabilityBonus, period: ReportPeriod): number {
  return bonus.status === 'active' && bonus.is_effective
    && bonus.effective_date >= format(period.from, 'yyyy-MM-dd')
    && bonus.effective_date <= format(period.to, 'yyyy-MM-dd') ? bonus.amount : 0;
}

export function computeCommissionTotalsWithRecaptures(
  earnings: CommissionEarning[],
  records: RecaptureActivity[] | null,
  period: ReportPeriod,
  bonuses: ProfitabilityBonus[] = [],
  volumeBonusActivity: VolumeBonusActivity[] = []
): AdjustedCommissionTotals {
  const totals = computeCommissionTotals(earnings);
  const bonusCents = bonuses.reduce((sum, bonus) => sum + Math.round(bonusPeriodContribution(bonus, period) * 100), 0);
  const profitabilityBonuses = bonusCents / 100;
  const from = format(period.from, 'yyyy-MM-dd');
  const to = format(period.to, 'yyyy-MM-dd');
  const volumeCents = volumeBonusActivity.reduce((sum, event) => event.date >= from && event.date <= to
    ? sum + Math.round(event.amount * 100) : sum, 0);
  const profitabilityVolumeBonusAdjustment = volumeCents / 100;
  if (volumeCents !== 0) {
    totals.loProfitability = (Math.round(totals.loProfitability * 100) + volumeCents) / 100;
  }
  if (bonusCents !== 0) {
    totals.loProfitability = (Math.round(totals.loProfitability * 100) + bonusCents) / 100;
  }
  if (records === null) {
    return { ...totals, profitabilityBonuses, profitabilityVolumeBonusAdjustment, profitabilityRecaptured: null };
  }
  let absorbedCents = 0;
  for (const record of records) {
    if (record.status !== 'submitted' || !recapturePostedInPeriod(record, period)) {
      continue;
    }
    for (const allocation of record.allocations) {
      if (allocation.kind === 'profitability') {
        absorbedCents += Math.round(allocation.amount * 100);
      }
    }
  }
  return {
    ...totals,
    profitabilityBonuses,
    profitabilityVolumeBonusAdjustment,
    loProfitability: absorbedCents === 0
      ? totals.loProfitability
      : (Math.round(totals.loProfitability * 100) - absorbedCents) / 100,
    profitabilityRecaptured: absorbedCents / 100
  };
}

/** Signed remaining balance, including credits and the one-cent settlement band. */
export function earningBalance(e: CommissionEarning): number {
  return earningSettlement(e).remaining;
}

export interface PlanBasisLabel {
  primary: string;
  hasPlan: boolean;
}

/** Comp-basis header label — "Plan: 1.25%" vs "No comp plan". Never "Partner". */
export function planBasisLabel(plan: CommissionPlanBasis | null): PlanBasisLabel {
  if (!plan || !plan.has_comp_plan) {
    return { primary: 'No comp plan', hasPlan: false };
  }
  const pct = plan.optimal_blue_current_percentage ?? plan.banked_compensation;
  const scheduleBits = [plan.schedule_name, plan.schedule_scope].filter(Boolean).join(', ');
  const pctText = pct !== null ? `${pct}%` : '';
  return {
    primary: scheduleBits ? `Plan: ${pctText} (${scheduleBits})` : `Plan: ${pctText}`.trim(),
    hasPlan: true
  };
}

/** Basis label for an admin-set ReportUser (reuses planBasisLabel). */
export function planBasisLabelForUser(user: ReportUser | null): PlanBasisLabel {
  if (!user) {
    return { primary: 'No comp plan', hasPlan: false };
  }
  if (user.has_comp_plan === null) {
    return { primary: 'Comp plan unavailable', hasPlan: false };
  }
  return planBasisLabel({
    has_comp_plan: user.has_comp_plan,
    banked_compensation: user.banked_compensation,
    optimal_blue_current_percentage: user.optimal_blue_current_percentage,
    schedule_name: user.schedule_name,
    schedule_scope: user.schedule_scope
  });
}

// ---- admin all-records: roster + splits attribution -------------------------

/**
 * Splits generated FROM this earning's loan by this earning's recipient.
 * Grouped by `source_officer_id` + `loan_number` (not loan_number alone) so
 * all-records mode attaches splits to the LO who generated them. In self mode
 * the recipient == the source, so behavior is unchanged.
 */
export function splitsFor(
  context: CommissionContextRow[],
  loUserId: string | null | undefined,
  loanNumber: string
): CommissionContextRow[] {
  const noFilter = loUserId === null || loUserId === undefined;
  return context.filter(
    (c) => c.loan_number === loanNumber && (noFilter || (c.source_officer_id ?? null) === loUserId)
  );
}

export interface RosterRow {
  profitabilityBonuses?: number;
  user_id: string;
  name: string | null;
  employment_status: string | null;
  has_comp_plan: boolean | null;
  /** Earnings-row count (label "Records" — NOT distinct loans). */
  record_count: number;
  earned: number;
  paid: number;
  owed: number;
  loProfitability: number;
  profitabilityRecaptured: number | null;
}

/** Keep authorized recapture-only recipients visible even without a period-funded payout. */
export function commissionReportUsersWithRecaptures(
  reportUsers: ReportUser[],
  records: RecaptureRecord[] | null,
  period: ReportPeriod
): ReportUser[] {
  const users = new Map(reportUsers.map(user => [user.user_id, user]));
  for (const record of records ?? []) {
    if (users.has(record.recipient_id)
      || !['submitted', 'reversed'].includes(record.status)
      || !recapturePostedInPeriod(record, period)) {
      continue;
    }
    users.set(record.recipient_id, {
      user_id: record.recipient_id,
      bonus_eligible: false,
      name: record.recipient_name || null,
      email: null,
      employment_status: null,
      has_comp_plan: null,
      optimal_blue_current_percentage: null,
      banked_compensation: null,
      schedule_name: null,
      schedule_scope: null
    });
  }
  return [...users.values()];
}

/**
 * Per-user roster including **zero-record users** (from `report_users`).
 * Earnings plus period recapture activity. `context` (splits) is display-only
 * and never counted here (a split is the recipient's earning elsewhere).
 */
export function buildRoster(
  reportUsers: ReportUser[],
  earnings: CommissionEarning[],
  recapture?: { records: RecaptureRecord[] | null; period: ReportPeriod; bonuses?: ProfitabilityBonus[]; volumeBonusActivity?: VolumeBonusActivity[] }
): RosterRow[] {
  const byUser = new Map<string, CommissionEarning[]>();
  for (const e of earnings) {
    if (!e.lo_user_id) {
      continue;
    }
    const list = byUser.get(e.lo_user_id) ?? [];
    list.push(e);
    byUser.set(e.lo_user_id, list);
  }

  const recapturesByUser = new Map<string, RecaptureRecord[]>();
  for (const record of recapture?.records ?? []) {
    const list = recapturesByUser.get(record.recipient_id) ?? [];
    list.push(record);
    recapturesByUser.set(record.recipient_id, list);
  }

  const users = recapture
    ? commissionReportUsersWithRecaptures(reportUsers, recapture.records, recapture.period)
    : reportUsers;
  return users.map((u) => {
    const rows = byUser.get(u.user_id) ?? [];
    const totals = recapture
      ? computeCommissionTotalsWithRecaptures(rows, recapture.records === null ? null : recapturesByUser.get(u.user_id) ?? [], recapture.period,
        recapture.bonuses?.filter(bonus => bonus.recipient_id === u.user_id), recapture.volumeBonusActivity?.filter(event => event.recipient_id === u.user_id))
      : { ...computeCommissionTotals(rows), profitabilityRecaptured: 0, profitabilityBonuses: 0 };
    return {
      user_id: u.user_id,
      name: u.name,
      employment_status: u.employment_status,
      has_comp_plan: u.has_comp_plan,
      record_count: rows.filter(earning => earning.payout_type !== 'volume_bonus').length,
      earned: totals.totalEarned,
      paid: totals.totalPaid,
      owed: totals.outstanding,
      loProfitability: totals.loProfitability,
      profitabilityBonuses: totals.profitabilityBonuses,
      profitabilityRecaptured: totals.profitabilityRecaptured
    };
  });
}

export interface RosterTotals {
  earned: number;
  paid: number;
  owed: number;
  loProfitability: number;
  profitabilityRecaptured: number | null;
  userCount: number;
  recordCount: number;
}

export function computeRosterTotals(roster: RosterRow[]): RosterTotals {
  let earned = 0;
  let paid = 0;
  let owed = 0;
  let loProfitability = 0;
  let profitabilityRecaptured: number | null = 0;
  let recordCount = 0;
  for (const r of roster) {
    earned += r.earned;
    paid += r.paid;
    owed += r.owed;
    loProfitability += r.loProfitability;
    profitabilityRecaptured = profitabilityRecaptured === null || r.profitabilityRecaptured === null
      ? null
      : profitabilityRecaptured + r.profitabilityRecaptured;
    recordCount += r.record_count;
  }
  return { earned, paid, owed, loProfitability, profitabilityRecaptured, userCount: roster.length, recordCount };
}
